import { useMemo } from 'react';
import { cn } from '@/lib/utils';

/*
 * Sparkline — DESIGN_SYSTEM.md §2.4
 *
 * Inline mini chart, no axes. Renders a smoothed SVG path through the
 * given numeric series. Used inside KpiTile but also stand-alone.
 *
 * Tone maps to a stroke color:
 *   brand   → brand-500
 *   success → success
 *   warn    → warn
 *   danger  → danger
 *   muted   → line-strong
 */

export type SparklineTone = 'brand' | 'success' | 'warn' | 'danger' | 'muted';

interface SparklineProps {
  /** Numeric series, in order. Needs at least 2 points to draw. */
  series: number[];
  tone?: SparklineTone;
  /** Pixel dimensions. Default 80×24. */
  width?: number;
  height?: number;
  /** Render a soft fill below the line. */
  fill?: boolean;
  className?: string;
  ariaLabel?: string;
}

const STROKE_TONE: Record<SparklineTone, string> = {
  brand:   'stroke-brand-500',
  success: 'stroke-success',
  warn:    'stroke-warn',
  danger:  'stroke-danger',
  muted:   'stroke-line-strong',
};

const FILL_TONE: Record<SparklineTone, string> = {
  brand:   'fill-brand-500/15',
  success: 'fill-success/15',
  warn:    'fill-warn/15',
  danger:  'fill-danger/15',
  muted:   'fill-line-strong/15',
};

export function Sparkline({
  series,
  tone = 'brand',
  width = 80,
  height = 24,
  fill = false,
  className,
  ariaLabel,
}: SparklineProps) {
  const { linePath, areaPath } = useMemo(() => {
    if (series.length < 2) return { linePath: '', areaPath: '' };
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1;
    const stepX = width / (series.length - 1);
    const points = series.map((v, i) => {
      const x = i * stepX;
      // Invert Y: higher value → lower y.
      const y = height - ((v - min) / range) * height;
      return [x, y] as const;
    });
    const linePath = points
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`)
      .join(' ');
    const areaPath = `${linePath} L${width} ${height} L0 ${height} Z`;
    return { linePath, areaPath };
  }, [series, width, height]);

  if (series.length < 2) return null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      className={cn('overflow-visible', className)}
    >
      {fill && <path d={areaPath} className={cn(FILL_TONE[tone], 'stroke-none')} />}
      <path
        d={linePath}
        className={cn(STROKE_TONE[tone], 'fill-none')}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
